/**
 * PredictedROI — V146.2 (improvement).
 *
 * Takes the top 3 ranked agents, computes:
 *   monthly_invest = sum of prices
 *   monthly_value  = sum of eur_saved_month
 *   payback_days   = (monthly_invest / monthly_value) × 30
 *   year_1_net     = monthly_value × 12 − monthly_invest × 12
 *
 * Visualized as horizontal stack bar (amber invest vs emerald value)
 * + payback days callout.
 */
import { TrendingUp, Clock, Wallet } from "lucide-react";

export default function PredictedROI({ results }) {
  if (!Array.isArray(results) || results.length < 2) return null;
  const top3 = results.filter(r => !r.coming_soon).slice(0, 3);
  if (top3.length < 2) return null;

  const monthly_invest = top3.reduce((s, r) => s + (r.price_eur_month || 0), 0);
  const monthly_value = top3.reduce((s, r) => s + (r.eur_saved_month || 0), 0);
  if (monthly_value <= 0) return null;

  const payback_days = Math.max(1, Math.round((monthly_invest / monthly_value) * 30));
  const year_1_net = monthly_value * 12 - monthly_invest * 12;
  const roi_pct = Math.round((monthly_value / Math.max(monthly_invest, 1)) * 100);

  // Bar widths (normalize to 100%)
  const total = monthly_invest + monthly_value;
  const invest_pct = Math.max(4, Math.round((monthly_invest / total) * 100));
  const value_pct = 100 - invest_pct;

  return (
    <section
      data-testid="predicted-roi"
      className="border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-5 sm:p-7 mb-8"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] font-black text-emerald-400 mb-2">
        <TrendingUp className="w-3 h-3 inline mr-1" /> Predikovaná návratnosť · top 3 agenti
      </p>
      <h2 className="text-xl sm:text-2xl lg:text-3xl font-black leading-tight mb-4">
        Investícia sa ti vráti za{" "}
        <span className="text-emerald-400">{payback_days} {payback_days === 1 ? "deň" : payback_days < 5 ? "dni" : "dní"}</span>.
      </h2>

      {/* Stack bar */}
      <div className="mb-2 flex h-8 overflow-hidden border border-gray-800" data-testid="roi-stackbar">
        <div
          className="bg-amber-500 flex items-center justify-end pr-3 text-[10px] font-black text-black uppercase tracking-widest"
          style={{ width: `${invest_pct}%` }}
          title="Investícia"
        >
          €{monthly_invest}
        </div>
        <div
          className="bg-emerald-500 flex items-center justify-start pl-3 text-[10px] font-black text-black uppercase tracking-widest"
          style={{ width: `${value_pct}%` }}
          title="Hodnota"
        >
          €{monthly_value.toLocaleString("sk-SK")}
        </div>
      </div>
      <div className="flex justify-between text-[9px] uppercase tracking-widest font-black mb-5">
        <span className="text-amber-300"><Wallet className="w-3 h-3 inline" /> Investícia / mes</span>
        <span className="text-emerald-300">Hodnota / mes <Clock className="w-3 h-3 inline" /></span>
      </div>

      {/* Numbers */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="border border-gray-800 p-3" data-testid="roi-payback">
          <p className="text-[9px] uppercase tracking-widest text-gray-500 font-black">Payback</p>
          <p className="text-2xl font-black text-emerald-400">{payback_days} dní</p>
        </div>
        <div className="border border-gray-800 p-3" data-testid="roi-multiplier">
          <p className="text-[9px] uppercase tracking-widest text-gray-500 font-black">ROI ratio</p>
          <p className="text-2xl font-black text-emerald-400">{roi_pct}%</p>
        </div>
        <div className="border border-gray-800 p-3 col-span-2 sm:col-span-1" data-testid="roi-year1">
          <p className="text-[9px] uppercase tracking-widest text-gray-500 font-black">Čistý zisk / 1. rok</p>
          <p className="text-2xl font-black text-emerald-400">€{year_1_net.toLocaleString("sk-SK")}</p>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
        💡 Predikcia podľa avg úspor agentov × tvoje detekované signály. Skutočné čísla závisia od veľkosti tvojho biznisu.
      </p>
    </section>
  );
}
